import React, { type FC } from 'react';
import { connect } from 'react-redux';
import { List } from 'immutable';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../../../../../types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { useTranslation } from 'app/lang';
import { getLineNameByLang } from 'app/lib/betting/get-line-name-by-lang';
import { SPORT_LINE_KIND } from 'app/lib/matchGridStakes';
import { InnerLineEntry, LineEntry } from 'app/redux/betting-presentation/grid/selectors';

import { MATCH_HEADER } from '../../../../../../constants';
import { getDataGaEventByNamespace } from '../../../../../../utils/data-ga-event-by-namespace';
import ExpansionButton from '../../../../../ExpansionButton';
import MatchLine from '../MatchLine';
import RegularLine from '../RegularLine';

import dictionary from './InnerHeaderHeadToHead.dictionary.json';

import styles from './InnerHeaderHeadToHead.module.scss';

type StoreProps = {
  namespace: ContextNamespace;
  isMatchLine: boolean;
  hasStakeSlots: boolean;
  mainCellWidth: string;
  visibleStakeIds: List<number | null>;
};

type InnerHeaderHeadToHeadProps = {
  matchId: number | null;
  lineId: number | null;
  lineKind: InnerLineEntry['kind'];
  lineNames: LineEntry['names'];
  expanded: boolean;
  expandClick: VoidFunction;
};

const InnerHeaderHeadToHead: FC<InnerHeaderHeadToHeadProps & StoreProps> = ({
  namespace,
  expanded,
  matchId,
  lineNames,
  lineKind,
  isMatchLine,
  hasStakeSlots,
  visibleStakeIds,
  expandClick,
}) => {
  const { lang } = useTranslation();
  const currentLineKind = lineKind || isMatchLine ? SPORT_LINE_KIND.main : SPORT_LINE_KIND.regular;
  const lineName = getLineNameByLang(lineNames, lang);
  const dataGaValues = expanded ? {
    ['data-ga-event']: getDataGaEventByNamespace(namespace, 'expandBidsGroupsDetails'),
    ['data-ga-event-value']: getLineNameByLang(lineNames, 'en'),
  } : undefined;

  const getContentByKind = () => {
    if (currentLineKind === SPORT_LINE_KIND.main) {
      return (
        <MatchLine hasStakeSlots={hasStakeSlots}
                   expanded={expanded}
                   title={MATCH_HEADER.title} />
      );
    }

    if (currentLineKind === SPORT_LINE_KIND.additional) {
      return (
        <MatchLine hasStakeSlots={hasStakeSlots}
                   expanded={expanded}
                   title={dictionary.additionalLineTitle} />
      );
    }

    if (currentLineKind === SPORT_LINE_KIND.regular && matchId) {
      return (
        <RegularLine visibleStakeIds={visibleStakeIds}
                     matchId={matchId} />
      );
    }
  };

  return (
    <>
      <div className={styles.lineDescription}>
        {lineName}
      </div>

      {getContentByKind()}

      {hasStakeSlots
        ? (
          <ExpansionButton
            dataGaValues={dataGaValues}
            className={styles.border}
            expansionState={expanded ? 2 : 0}
            onClick={expandClick} />
        )
        : <div className={styles.emptyBlock} />
      }
    </>
  );
};

const createMapStateToProps = (
  state: ReduxState,
  {
    $betting: {
      namespace,
      grid: {
        selectors: {
          mainLineId,
          stakeSlotsSelector,
          mainCellWidthSelector,
          visibleLineStakeIdsSelector,
        },
      },
      matchGrid,
    },
    lineId,
    matchId,
  }: BettingContextProps & InnerHeaderHeadToHeadProps,
) => (): StoreProps => {
  const isPrematch = namespace === ContextNamespace.PREMATCH;
  // TODO: как только удалят isMain из InnerLineEntry, тогда matchLineId заменить на lineKind
  const prematchMatchesMainLineId = matchGrid?.selectors?.mainLineId;
  const prematchMatchesStakeSlots = matchGrid?.selectors?.stakeSlotsSelector;
  const prematchMatchesMainCellWidth = matchGrid?.selectors?.mainCellWidthSelector;
  const prematchMatchesVisibleLineStakeIds = matchGrid?.selectors?.visibleLineStakeIdsSelector;

  const matchLineId = (isPrematch && prematchMatchesMainLineId)
    ? prematchMatchesMainLineId(state, { matchId })
    : mainLineId(state, { matchId });

  const stakeSlots = (isPrematch && prematchMatchesStakeSlots)
    ? prematchMatchesStakeSlots(state, lineId, matchId)
    : stakeSlotsSelector(state, lineId, matchId);

  const mainCellWidth = (isPrematch && prematchMatchesMainCellWidth)
    ? prematchMatchesMainCellWidth(state, lineId, matchId)
    : mainCellWidthSelector(state, lineId, matchId);

  const visibleStakeIds = (isPrematch && prematchMatchesVisibleLineStakeIds)
    ? prematchMatchesVisibleLineStakeIds(state, lineId, matchId)
    : visibleLineStakeIdsSelector(state, lineId, matchId);

  return {
    namespace,
    isMatchLine: matchLineId === lineId,
    hasStakeSlots: stakeSlots?.size !== 0,
    mainCellWidth: `${mainCellWidth}px`,
    visibleStakeIds,
  };
};

export default compose<FC<InnerHeaderHeadToHeadProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(InnerHeaderHeadToHead);
